import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  ConsoleState,
  ConsoleActions,
  ConsoleMessage,
  ConsoleGroup,
  MessageType,
  MessageSource,
  StackFrame,
  CommandHistoryEntry,
  DEFAULT_CONSOLE_FILTER,
  DEFAULT_CONSOLE_SETTINGS,
  MAX_MESSAGES,
  MAX_HISTORY_ENTRIES,
  MAX_STACK_FRAMES,
} from '../../core/types/console.types';

const generateId = () => Math.random().toString(36).substring(2, 11);

const formatValue = (value: unknown): string => {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'function') return `[Function: ${value.name || 'anonymous'}]`;
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    if (value.length <= 5) return `[${value.map(formatValue).join(', ')}]`;
    return `Array(${value.length})`;
  }
  if (typeof value === 'object') {
    try {
      const keys = Object.keys(value);
      if (keys.length === 0) return '{}';
      if (keys.length <= 3) {
        return `{${keys.map(k => `${k}: ${formatValue((value as Record<string, unknown>)[k])}`).join(', ')}}`;
      }
      return `Object {${keys.slice(0, 3).join(', ')}, ...}`;
    } catch {
      return '[Object]';
    }
  }
  return String(value);
};

const formatArgs = (args: unknown[]): string => {
  return args.map(formatValue).join(' ');
};

const parseStackTrace = (stack?: string): StackFrame[] => {
  if (!stack) return [];

  const frames: StackFrame[] = [];
  const lines = stack.split('\n').slice(1); // Skip the error message line

  for (const line of lines) {
    if (frames.length >= MAX_STACK_FRAMES) break;

    // Match patterns like "at functionName (fileName:line:col)" or "at fileName:line:col"
    const match = line.match(/at\s+(?:(.+?)\s+\()?(.+?):(\d+):(\d+)\)?/);
    if (match) {
      frames.push({
        functionName: match[1] || '<anonymous>',
        fileName: match[2],
        lineNumber: parseInt(match[3], 10),
        columnNumber: parseInt(match[4], 10),
        source: line.trim(),
      });
    }
  }

  return frames;
};

const findLastIdenticalMessage = (
  messages: ConsoleMessage[],
  type: MessageType,
  message: string
): ConsoleMessage | null => {
  if (messages.length === 0) return null;
  const lastMsg = messages[messages.length - 1];
  if (lastMsg.type === type && lastMsg.message === message) {
    return lastMsg;
  }
  return null;
};

interface ConsoleStore extends ConsoleState, ConsoleActions {}

export const useConsoleStore = create<ConsoleStore>()(
  immer((set, get) => ({
      // Initial state
      messages: [],
      filteredMessages: [],
      groups: [],
      currentGroupId: null,
      filter: DEFAULT_CONSOLE_FILTER,
      settings: DEFAULT_CONSOLE_SETTINGS,
      commandHistory: [],
      commandHistoryIndex: -1,
      currentCommand: '',
      isExecuting: false,
      isScrolledToBottom: true,
      selectedMessageId: null,
      isSettingsOpen: false,
      infoCount: 0,
      warningCount: 0,
      errorCount: 0,
      debugCount: 0,

      // Message methods
      addMessage: (type, message, options = {}) => {
        set((state) => {
          const { collapseIdentical, maxMessages } = state.settings;

          // Check for identical message to collapse
          if (collapseIdentical && !options.stackTrace) {
            const lastMsg = findLastIdenticalMessage(state.messages, type, message);
            if (lastMsg) {
              const idx = state.messages.findIndex(m => m.id === lastMsg.id);
              if (idx >= 0) {
                state.messages[idx].count += 1;
                state.messages[idx].timestamp = Date.now();
                return;
              }
            }
          }

          const newMessage: ConsoleMessage = {
            id: generateId(),
            type,
            source: options.source || 'script',
            timestamp: Date.now(),
            message,
            formattedMessage: options.formattedMessage,
            data: options.data,
            stackTrace: options.stackTrace,
            count: 1,
            isExpanded: false,
            groupId: state.currentGroupId || undefined,
            groupLevel: state.currentGroupId
              ? (state.groups.find(g => g.id === state.currentGroupId)?.level ?? 0) + 1
              : 0,
            fileReference: options.fileReference,
          };

          state.messages.push(newMessage);

          // Enforce max messages
          while (state.messages.length > maxMessages) {
            const removed = state.messages.shift();
            if (removed) {
              // Update counters
              switch (removed.type) {
                case 'info': state.infoCount = Math.max(0, state.infoCount - removed.count); break;
                case 'warning': state.warningCount = Math.max(0, state.warningCount - removed.count); break;
                case 'error': state.errorCount = Math.max(0, state.errorCount - removed.count); break;
                case 'debug': state.debugCount = Math.max(0, state.debugCount - removed.count); break;
              }
            }
          }

          // Update counters
          switch (type) {
            case 'info': state.infoCount += 1; break;
            case 'warning': state.warningCount += 1; break;
            case 'error': state.errorCount += 1; break;
            case 'debug': state.debugCount += 1; break;
          }
        });

        // Apply filter
        get().applyFilter();
      },

      addSystemMessage: (message, type = 'info') => {
        get().addMessage(type, message, { source: 'system' });
      },

      log: (...args) => {
        const message = formatArgs(args);
        get().addMessage('log', message, { data: args, source: 'script' });
      },

      info: (...args) => {
        const message = formatArgs(args);
        get().addMessage('info', message, { data: args, source: 'script' });
      },

      warn: (...args) => {
        const message = formatArgs(args);
        const stack = new Error().stack;
        get().addMessage('warning', message, {
          data: args,
          source: 'script',
          stackTrace: parseStackTrace(stack),
        });
      },

      error: (...args) => {
        const message = formatArgs(args);
        const stack = args[0] instanceof Error ? args[0].stack : new Error().stack;
        get().addMessage('error', message, {
          data: args,
          source: 'script',
          stackTrace: parseStackTrace(stack),
          fileReference: args[0] instanceof Error ? {
            fileName: '<runtime>',
            lineNumber: 0,
            columnNumber: 0,
          } : undefined,
        });
      },

      debug: (...args) => {
        const message = formatArgs(args);
        get().addMessage('debug', message, { data: args, source: 'script' });
      },

      table: (data, columns) => {
        let message: string;
        if (Array.isArray(data)) {
          message = `Table: ${data.length} rows`;
        } else if (typeof data === 'object' && data !== null) {
          message = `Table: ${Object.keys(data).length} entries`;
        } else {
          message = 'Invalid table data';
        }
        get().addMessage('table', message, { data: [data, columns], source: 'script' });
      },

      // Group methods
      group: (label = 'Group') => {
        set((state) => {
          const group: ConsoleGroup = {
            id: generateId(),
            label,
            isCollapsed: false,
            parentId: state.currentGroupId || undefined,
            level: state.currentGroupId
              ? (state.groups.find(g => g.id === state.currentGroupId)?.level ?? 0) + 1
              : 0,
          };
          state.groups.push(group);
          state.currentGroupId = group.id;
        });
        get().addMessage('group', label, { source: 'script' });
      },

      groupCollapsed: (label = 'Group') => {
        set((state) => {
          const group: ConsoleGroup = {
            id: generateId(),
            label,
            isCollapsed: true,
            parentId: state.currentGroupId || undefined,
            level: state.currentGroupId
              ? (state.groups.find(g => g.id === state.currentGroupId)?.level ?? 0) + 1
              : 0,
          };
          state.groups.push(group);
          state.currentGroupId = group.id;
        });
        get().addMessage('group', label, { source: 'script' });
      },

      groupEnd: () => {
        set((state) => {
          if (state.currentGroupId) {
            const currentGroup = state.groups.find(g => g.id === state.currentGroupId);
            state.currentGroupId = currentGroup?.parentId || null;
          }
        });
        get().addMessage('groupEnd', '', { source: 'script' });
      },

      toggleGroup: (groupId) => {
        set((state) => {
          const group = state.groups.find(g => g.id === groupId);
          if (group) {
            group.isCollapsed = !group.isCollapsed;
          }
        });
      },

      // Clear methods
      clear: () => {
        set((state) => {
          state.messages = [];
          state.filteredMessages = [];
          state.groups = [];
          state.currentGroupId = null;
          state.infoCount = 0;
          state.warningCount = 0;
          state.errorCount = 0;
          state.debugCount = 0;
        });
        get().addSystemMessage('Console was cleared', 'info');
      },

      clearErrors: () => {
        set((state) => {
          state.messages = state.messages.filter(m => m.type !== 'error');
          state.errorCount = 0;
        });
        get().applyFilter();
      },

      // Filter methods
      setSearchText: (text) => {
        set((state) => {
          state.filter.searchText = text;
        });
        get().applyFilter();
      },

      toggleMessageType: (type) => {
        set((state) => {
          switch (type) {
            case 'info':
              state.filter.showInfo = !state.filter.showInfo;
              break;
            case 'warning':
              state.filter.showWarnings = !state.filter.showWarnings;
              break;
            case 'error':
              state.filter.showErrors = !state.filter.showErrors;
              break;
            case 'debug':
              state.filter.showDebug = !state.filter.showDebug;
              break;
            case 'log':
              state.filter.showLogs = !state.filter.showLogs;
              break;
          }
        });
        get().applyFilter();
      },

      setSourceFilter: (source) => {
        set((state) => {
          state.filter.sourceFilter = source;
        });
        get().applyFilter();
      },

      applyFilter: () => {
        set((state) => {
          const { searchText, showInfo, showWarnings, showErrors, showDebug, showLogs, sourceFilter } = state.filter;
          const searchLower = searchText.toLowerCase();

          state.filteredMessages = state.messages.filter((msg) => {
            // Type filter
            switch (msg.type) {
              case 'info': if (!showInfo) return false; break;
              case 'warning': if (!showWarnings) return false; break;
              case 'error': if (!showErrors) return false; break;
              case 'debug': if (!showDebug) return false; break;
              case 'log': if (!showLogs) return false; break;
            }

            // Source filter
            if (sourceFilter !== 'all' && msg.source !== sourceFilter) {
              return false;
            }

            // Search filter
            if (searchText && !msg.message.toLowerCase().includes(searchLower)) {
              return false;
            }

            return true;
          });
        });
      },

      // Settings methods
      updateSettings: (settings) => {
        set((state) => {
          state.settings = { ...state.settings, ...settings };
        });
      },

      toggleSettings: () => {
        set((state) => {
          state.isSettingsOpen = !state.isSettingsOpen;
        });
      },

      // Command methods
      executeCommand: async (command) => {
        const trimmedCommand = command.trim();
        if (!trimmedCommand) return;

        set((state) => {
          state.isExecuting = true;
          state.currentCommand = '';
        });

        // Add command to history
        const historyEntry: CommandHistoryEntry = {
          id: generateId(),
          command: trimmedCommand,
          timestamp: Date.now(),
        };

        // Log the command
        get().addMessage('log', `> ${trimmedCommand}`, { source: 'command' });

        try {
          // Create a safe execution context
          const gameObjects = (window as unknown as { __EDITOR_GAME_OBJECTS__?: unknown }).__EDITOR_GAME_OBJECTS__ || {};
          const scene = (window as unknown as { __EDITOR_SCENE__?: unknown }).__EDITOR_SCENE__ || {};

          // Create console proxy for command context
          const consoleProxy = {
            log: get().log,
            info: get().info,
            warn: get().warn,
            error: get().error,
            debug: get().debug,
            table: get().table,
            group: get().group,
            groupCollapsed: get().groupCollapsed,
            groupEnd: get().groupEnd,
            clear: get().clear,
          };

          // Evaluate the command
          const evalFunc = new Function(
            'console',
            'gameObjects',
            'scene',
            `try {
              const result = eval(${JSON.stringify(trimmedCommand)});
              return result;
            } catch (e) {
              throw e;
            }`
          );

          const result = evalFunc(consoleProxy, gameObjects, scene);

          // Log the result if not undefined
          if (result !== undefined) {
            get().addMessage('log', formatValue(result), { source: 'command' });
          }

          historyEntry.result = result;
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : String(err);
          get().error(errorMessage);
          historyEntry.error = errorMessage;
        }

        set((state) => {
          state.isExecuting = false;
          state.commandHistory.unshift(historyEntry);

          // Limit history size
          if (state.commandHistory.length > MAX_HISTORY_ENTRIES) {
            state.commandHistory = state.commandHistory.slice(0, MAX_HISTORY_ENTRIES);
          }

          state.commandHistoryIndex = -1;
        });
      },

      setCurrentCommand: (command) => {
        set((state) => {
          state.currentCommand = command;
        });
      },

      navigateHistory: (direction) => {
        set((state) => {
          const historyLength = state.commandHistory.length;
          if (historyLength === 0) return;

          if (direction === 'up') {
            if (state.commandHistoryIndex < historyLength - 1) {
              state.commandHistoryIndex += 1;
              state.currentCommand = state.commandHistory[state.commandHistoryIndex].command;
            }
          } else {
            if (state.commandHistoryIndex > 0) {
              state.commandHistoryIndex -= 1;
              state.currentCommand = state.commandHistory[state.commandHistoryIndex].command;
            } else if (state.commandHistoryIndex === 0) {
              state.commandHistoryIndex = -1;
              state.currentCommand = '';
            }
          }
        });
      },

      clearHistory: () => {
        set((state) => {
          state.commandHistory = [];
          state.commandHistoryIndex = -1;
        });
      },

      // UI methods
      setScrolledToBottom: (value) => {
        set((state) => {
          state.isScrolledToBottom = value;
        });
      },

      selectMessage: (id) => {
        set((state) => {
          state.selectedMessageId = id;
        });
      },

      copyMessage: (id) => {
        const message = get().messages.find(m => m.id === id);
        if (message) {
          navigator.clipboard.writeText(message.message);
        }
      },

      expandMessage: (id) => {
        set((state) => {
          const message = state.messages.find(m => m.id === id);
          if (message) {
            message.isExpanded = true;
          }
        });
      },

      collapseMessage: (id) => {
        set((state) => {
          const message = state.messages.find(m => m.id === id);
          if (message) {
            message.isExpanded = false;
          }
        });
      },

      toggleMessageExpanded: (id) => {
        set((state) => {
          const message = state.messages.find(m => m.id === id);
          if (message) {
            message.isExpanded = !message.isExpanded;
          }
        });
      },

      // Export
      exportLog: () => {
        const { messages, settings } = get();
        const lines: string[] = [];

        for (const msg of messages) {
          let line = '';

          if (settings.showTimestamps) {
            const date = new Date(msg.timestamp);
            line += `[${date.toLocaleTimeString()}] `;
          }

          line += `[${msg.type.toUpperCase()}] `;
          line += msg.message;

          if (msg.count > 1) {
            line += ` (x${msg.count})`;
          }

          lines.push(line);

          if (settings.showStackTraces && msg.stackTrace?.length) {
            for (const frame of msg.stackTrace) {
              lines.push(`    at ${frame.functionName} (${frame.fileName}:${frame.lineNumber}:${frame.columnNumber})`);
            }
          }
        }

        return lines.join('\n');
      },

      // Project state methods
      loadProjectState: (projectState: {
        commandHistory?: CommandHistoryEntry[];
        filters?: {
          showLogs?: boolean;
          showWarnings?: boolean;
          showErrors?: boolean;
        };
      }) => {
        set((state) => {
          state.commandHistory = projectState.commandHistory || [];
          if (projectState.filters) {
            state.filter.showLogs = projectState.filters.showLogs ?? true;
            state.filter.showWarnings = projectState.filters.showWarnings ?? true;
            state.filter.showErrors = projectState.filters.showErrors ?? true;
          }
        });
        // Re-apply filters after loading
        get().applyFilter();
      },

      getProjectState: () => {
        const state = get();
        return {
          // Store limited command history (last 50)
          commandHistory: (state.commandHistory || []).slice(0, 50),
          filters: {
            showLogs: state.filter?.showLogs ?? true,
            showWarnings: state.filter?.showWarnings ?? true,
            showErrors: state.filter?.showErrors ?? true,
          },
        };
      },

      resetProjectState: () => {
        set((state) => {
          state.messages = [];
          state.filteredMessages = [];
          state.commandHistory = [];
          state.commandHistoryIndex = -1;
          state.groups = [];
          state.currentGroupId = null;
          state.infoCount = 0;
          state.warningCount = 0;
          state.errorCount = 0;
          state.debugCount = 0;
        });
      },
    }))
);
